/**
 * Hook per proteggere la compilazione di form dentro un modal:
 *  - Auto-salva la bozza su localStorage (debounced ~400ms)
 *  - Al mount, se esiste una bozza < 24h la propone in ripristino
 *  - `beforeunload` avvisa se ci sono modifiche non salvate (refresh/tab close)
 *  - Fornisce handler backdrop che chiudono SOLO se mousedown+mouseup avvengono
 *    entrambi sul backdrop stesso (drag/selezione di testo NON chiude più)
 *  - `safeClose()` chiede conferma quando il form è dirty
 *  - `clearDraft()` da chiamare quando il salvataggio riesce
 *
 * Uso tipico:
 *   const { clearDraft, backdropHandlers, safeClose } = useFormDraft({
 *     storageKey: "draft:bolla:new",
 *     form, setForm,
 *     isDirty: (f) => Boolean(f.fornitore || f.materiale || f.numero_bolla),
 *     onClose,
 *   });
 *
 *   <div className="fixed inset-0 ..." {...backdropHandlers}>
 *     <div onClick={(e) => e.stopPropagation()}>
 *       <button onClick={safeClose}>X</button>
 *       ...
 *     </div>
 *   </div>
 */
import { useEffect, useRef } from "react";

const DRAFT_TTL_MS = 24 * 60 * 60 * 1000; // 24h

export default function useFormDraft({
  storageKey,
  form,
  setForm,
  isDirty,
  onClose,
  enabled = true,
}) {
  const restored = useRef(false);
  const backdropMouseDownRef = useRef(null);
  const dirty = enabled && typeof isDirty === "function" ? isDirty(form) : false;

  // --- Ripristino bozza al primo render ---
  useEffect(() => {
    if (!enabled || restored.current || !storageKey) return;
    restored.current = true;
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      const data = parsed?.data;
      const ts = parsed?.ts;
      if (!data || !ts || Date.now() - ts > DRAFT_TTL_MS) {
        localStorage.removeItem(storageKey);
        return;
      }
      // Ripristina solo se il form corrente NON è già stato compilato
      if (typeof isDirty === "function" && isDirty(form)) return;
      const when = new Date(ts).toLocaleString("it-IT");
      // eslint-disable-next-line no-alert
      if (window.confirm(
        `Ho trovato una bozza non salvata del ${when}.\n\nVuoi ripristinarla?`
      )) {
        setForm(data);
      } else {
        localStorage.removeItem(storageKey);
      }
    } catch (_) {
      // localStorage non disponibile o JSON corrotto → ignora
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey, enabled]);

  // --- Autosave debounced ---
  useEffect(() => {
    if (!enabled || !storageKey) return undefined;
    // Non salvare se il form è vuoto (evitiamo di sporcare localStorage)
    if (typeof isDirty === "function" && !isDirty(form)) {
      return undefined;
    }
    const t = setTimeout(() => {
      try {
        localStorage.setItem(
          storageKey,
          JSON.stringify({ data: form, ts: Date.now() })
        );
      } catch (_) {
        // quota superata o storage disabilitato → ignora silenziosamente
      }
    }, 400);
    return () => clearTimeout(t);
  }, [storageKey, form, enabled, isDirty]);

  // --- Warning su refresh/chiusura tab se dirty ---
  useEffect(() => {
    if (!enabled || !dirty) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [enabled, dirty]);

  // --- Chiusura sicura: chiede conferma se dirty ---
  const safeClose = () => {
    if (dirty) {
      // eslint-disable-next-line no-alert
      const ok = window.confirm(
        "Hai dati non salvati. Chiudere davvero?\n\n" +
        "La bozza rimane salvata: potrai riprenderla riaprendo la finestra."
      );
      if (!ok) return;
    }
    if (typeof onClose === "function") onClose();
  };

  const clearDraft = () => {
    if (!storageKey) return;
    try { localStorage.removeItem(storageKey); } catch (_) { /* noop */ }
  };

  // --- Backdrop sicuro: chiude solo se mousedown+mouseup entrambi sul backdrop ---
  // Impedisce chiusure accidentali quando trascini il cursore fuori dal modal
  // (selezione testo, dropdown/datepicker che si chiudono, ecc.)
  const backdropHandlers = {
    onMouseDown: (e) => {
      backdropMouseDownRef.current = e.target === e.currentTarget;
    },
    onMouseUp: (e) => {
      const startedOnBackdrop = backdropMouseDownRef.current === true;
      backdropMouseDownRef.current = null;
      if (!startedOnBackdrop) return;
      if (e.target !== e.currentTarget) return;
      safeClose();
    },
  };

  return { clearDraft, safeClose, backdropHandlers, dirty };
}
